import React, { useState, useRef } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import {
  BellIcon,
  CheckBox,
  NewBackButton,
  VerticalDots,
} from "../../../../components/svgs";
import { Desktop } from "../../../../containers/common";
import { StyledText } from "../../../../components/common";
import { useTheme } from "../../../../hooks";
import NotificationModal from "../components/NotificationModal";
import NotificationCard from "../components/NotificationCard";
import OptionsModal from "../components/OptionsModal";
import useNewFeatureUpdate from "../hooks/useNewFeatureUpdate";

const NotificationHome = () => {
  const [spinModalOpen, setSpinModalOpen] = useState(false);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const longPressTimer = useRef(null);
  const isLongPressing = useRef(false);

  const {
    featureUpdates,
    deleteFeatureUpdate,
    markAsViewed,
    deleteSelectedFeatureUpdates,
    markSelectedAsViewed,
    notifications,
  } = useNewFeatureUpdate();

  const { themeContext, theme } = useTheme();
  let history = useHistory();
  let mashupBackground =
    theme.colors[themeContext.activeTheme]["mashupBackground"];

  const goHome = () => {
    history.push("/home?token=" + JSON.parse(localStorage.getItem("token")));
  };

  const now = new Date();
  const fiveDaysAgo = new Date();
  fiveDaysAgo.setDate(now.getDate() - 5);

  const recentUpdates = notifications.filter(
    (item) => new Date(item.createdAt) > fiveDaysAgo
  );
  const olderUpdates = notifications.filter(
    (item) => new Date(item.createdAt) <= fiveDaysAgo
  );

  const startLongPress = (id) => {
    isLongPressing.current = true;
    longPressTimer.current = setTimeout(() => {
      if (isLongPressing.current) {
        setShowCheckboxes(true);
        setSelectedNotifications([id]);
      }
    }, 500);
  };

  const stopLongPress = () => {
    isLongPressing.current = false;
    clearTimeout(longPressTimer.current);
  };

  const toggleSelectAll = () => {
    setSelectedNotifications((prev) =>
      prev.length === notifications.length ? [] : notifications.map((n) => n.id)
    );
  };

  const handleCheckBoxClick = (id, event) => {
    event.stopPropagation();
    toggleSelect(id);
  };

  const toggleSelect = (id) => {
    setSelectedNotifications((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleNotificationClick = async (id) => {
    if (!showCheckboxes) {
      const notification = notifications.find((item) => item.id === id);
      setSelectedNotification(notification);
      setSpinModalOpen(true);

      if (!notification.isViewed) {
        await markAsViewed(id);
      }
    } else {
      toggleSelect(id);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedNotifications.length === 0) return;

    try {
      const response = await deleteSelectedFeatureUpdates(
        selectedNotifications
      );

      if (!response || typeof response !== "object") {
        throw new Error("Invalid response from API");
      }

      if (response.success) {
        setSelectedNotifications([]);
        setShowCheckboxes(false);
        await featureUpdates.refetch();
      } else {
        console.error("Error deleting selected:", response.message);
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setOptionsModalOpen(false);
    }
  };

  const handleMarkSelectedAsViewed = async () => {
    if (selectedNotifications.length === 0) return;

    try {
      const response = await markSelectedAsViewed(selectedNotifications);

      if (!response || typeof response !== "object") {
        throw new Error("Invalid response from API");
      }

      if (response.success) {
        setSelectedNotifications([]);
        setShowCheckboxes(false);
        await featureUpdates.refetch();
      } else {
        console.error("Error marking as viewed:", response.message);
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setOptionsModalOpen(false);
    }
  };

  return (
    <>
      <Desktop>
        <MainHolder>
          <Children>
            <HeaderContainer>
              <BackButtonContainer onClick={goHome}>
                <NewBackButton />
              </BackButtonContainer>
              <StyledText color="#333">Notifications</StyledText>
              {notifications.length > 0 ? (
                <VerticalDots
                  onClick={(event) => {
                    event.stopPropagation();
                    setOptionsModalOpen(true);
                  }}
                />
              ) : (
                <div />
              )}
            </HeaderContainer>

            <DescriptionBody color={mashupBackground}>
              <HistoryContainer>
                {notifications.length === 0 ? (
                  <EmptyStateContainer>
                    <BellIcon />
                    You have no new notifications
                  </EmptyStateContainer>
                ) : (
                  <>
                    {recentUpdates.length > 0 && (
                      <>
                        <HistoryHeader>
                          <SectionTitle>Recent Updates</SectionTitle>
                          {showCheckboxes && (
                            <SelectAllContainer onClick={toggleSelectAll}>
                              <StyledText>Select All</StyledText>
                            </SelectAllContainer>
                          )}
                        </HistoryHeader>
                        {recentUpdates.map((item) => (
                          <NotificationRow
                            key={item.id}
                            onMouseDown={() => startLongPress(item.id)}
                            onMouseUp={stopLongPress}
                            onMouseLeave={stopLongPress}
                          >
                            {showCheckboxes && (
                              <CheckBox
                                checked={selectedNotifications.includes(
                                  item.id
                                )}
                                onClick={() => handleCheckBoxClick(item.id)}
                              />
                            )}
                            <NotificationCard
                              title={item.featureTitle}
                              createdAt={item.createdAt}
                              isViewed={item.isViewed}
                              onClick={() => handleNotificationClick(item.id)}
                            />
                          </NotificationRow>
                        ))}
                      </>
                    )}
                    {olderUpdates.length > 0 && (
                      <>
                        <SectionTitle>Older Updates</SectionTitle>
                        {olderUpdates.map((item) => (
                          <NotificationRow
                            key={item.id}
                            onMouseDown={() => startLongPress(item.id)}
                            onMouseUp={stopLongPress}
                            onMouseLeave={stopLongPress}
                            onTouchStart={() => startLongPress(item.id)}
                            onTouchEnd={stopLongPress}
                            onTouchCancel={stopLongPress}
                            onTouchMove={stopLongPress}
                          >
                            {showCheckboxes && (
                              <CheckBox
                                checked={selectedNotifications.includes(
                                  item.id
                                )}
                                onClick={() => handleCheckBoxClick(item.id)}
                              />
                            )}
                            <NotificationCard
                              title={item.featureTitle}
                              createdAt={item.createdAt}
                              isViewed={item.isViewed}
                              onClick={() => handleNotificationClick(item.id)}
                            />
                          </NotificationRow>
                        ))}
                      </>
                    )}
                  </>
                )}
              </HistoryContainer>
            </DescriptionBody>
          </Children>
        </MainHolder>
      </Desktop>
      <NotificationModal
        isOpen={spinModalOpen}
        onClose={() => setSpinModalOpen(false)}
        featureTitle={selectedNotification?.featureTitle || "No Title"}
        featureMessage={selectedNotification?.featureMessage || "No Message"}
        featureButtonText={selectedNotification?.featureButtonText || "OK"}
        featureUrl={selectedNotification?.featureButtonUrl || "/"}
        featureId={selectedNotification?.id}
        onDelete={deleteFeatureUpdate}
        releaseDate={selectedNotification?.releaseDate}
        releaseTime={selectedNotification?.releaseTime}
      />
      <OptionsModal
        isOpen={optionsModalOpen}
        onClose={() => setOptionsModalOpen(false)}
        onDeleteSelected={handleDeleteSelected}
        onMarkSelectedAsViewed={() => {
          handleMarkSelectedAsViewed();
        }}
      />
    </>
  );
};

export default NotificationHome;

const MainHolder = styled.div`
  overflow: hidden;
  background-color: white;
  height: 700px;
  position: relative;
  @media screen and (max-width: 480px) {
    height: 100vh;
  }
`;

const Children = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
`;

const HeaderContainer = styled.div`
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const BackButtonContainer = styled.div`
  background-color: #ffcc00;
  width: 30px;
  height: 30px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DescriptionBody = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ color }) => color};
  align-items: center;
`;

const HistoryContainer = styled.div`
  width: 90%;
  background-color: white;
  border-radius: 8px;
  margin: 20px;
  padding: 15px;
  overflow: auto;
`;

const EmptyStateContainer = styled.div`
  text-align: center;
  color: #d1cece;
  font-size: 16px;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 16px;
  height: 60vh;
`;

const SectionTitle = styled(StyledText)`
  font-weight: bold;
  font-size: 18px;
  margin: 15px 0;
  color: #333;
`;

const NotificationRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const SelectAllContainer = styled.div`
  cursor: pointer;
`;
